import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import Spinner from '../components/Spinner';
import SuccessNotification from '../components/notifications/SuccessNotification';
import CashCalculatorPopup, { BillCount } from '../components/CashCalculatorPopup';
import XReportPopup from '../components/XReportPopup';
import ReviewVariance from './ReviewVariance';
import { Shift } from '../interfaces';
import './CloseShift.css';
import { Property } from '../Toolbar';
import FinalizeDeposit from './FinalizeDeposit';
import ZReportView, { ZReportViewRef } from './ZReportView';
import EmailReportPopup from '../components/EmailReportPopup';
import { useAuth } from '../AuthContext';

interface OpenOrder {
    id: string;
    orderNumber: string;
    total: number;
    tableName: string;
}

const initialBills: BillCount[] = [
    { denomination: 100, count: 0, label: '100 BILLS' },
    { denomination: 50, count: 0, label: '50 BILLS' },
    { denomination: 20, count: 0, label: '20 BILLS' },
    { denomination: 10, count: 0, label: '10 BILLS' },
    { denomination: 5, count: 0, label: '5 BILLS' },
    { denomination: 1, count: 0, label: '1 BILLS' },
];

type ShiftStep = 'count' | 'review' | 'deposit' | 'complete';

const CloseShift: React.FC = () => {
    const { hasPerm } = useAuth();
    const can = (key: string) => !hasPerm || hasPerm(key);
    const navigate = useNavigate();
    const zReportRef = useRef<ZReportViewRef>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [currentShift, setCurrentShift] = useState<Shift | null>(null);
    const [closedShift, setClosedShift] = useState<Shift | null>(null);
    const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]);
    const [actualCashCount, setActualCashCount] = useState<number>(0);
    const [selectedTab, setSelectedTab] = useState<'count' | 'payouts'>('count');
    const [showSuccess, setShowSuccess] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const [isCashCalculatorOpen, setIsCashCalculatorOpen] = useState(false);
    const [isXReportOpen, setIsXReportOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState<ShiftStep>('count');
    const [varianceNotes, setVarianceNotes] = useState('');
    /** Who signed off on a cash discrepancy, for the shift record. */
    const [varianceApprovedBy, setVarianceApprovedBy] = useState('');
    // Cash calculator state - lifted up from the popup
    const [bills, setBills] = useState<BillCount[]>(initialBills);
    const [looseCoins, setLooseCoins] = useState<number>(0);
    const [currentProperty, setCurrentProperty] = useState<Property | null>(null);
    const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);

    const fetchProperty = async () => {
        const storedProperty = localStorage.getItem('currentProperty');
        if (storedProperty) {
            setCurrentProperty(JSON.parse(storedProperty));
        } else {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Company`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                setCurrentProperty(data);
            } catch (error) {
                console.error('Error fetching data: ', error);
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchProperty();
        };
        fetchData();
    }, []);

    useEffect(() => {
        fetchCurrentShift();
        fetchOpenOrders();
    }, []);

    const fetchCurrentShift = async () => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Shift/current`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setCurrentShift(data);
            }
        } catch (error) {
            console.error('Failed to fetch current shift:', error);
        } finally {
            setShowSpinner(false);
        }
    };

    const fetchOpenOrders = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/get-open-orders`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setOpenOrders(data);
            }
        } catch (error) {
            console.error('Failed to fetch open orders:', error);
        }
    };

    const handleReviewShift = () => {
        if (!can('pos-app.shift.end-day')) return;
        if (openOrders.length > 0) {
            setHeaderTitle("Cannot End Day");
            setContent("Please resolve all open orders before ending the day.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
            return;
        }
        setCurrentStep('review');
    };

    const handleVarianceNext = (notes: string, approvedBy: string) => {
        setVarianceNotes(notes);
        setVarianceApprovedBy(approvedBy);
        setCurrentStep('deposit');
    };

    const handleFinalizeDeposit = async (
        nextShiftFloat: number,
        bankDeposit: number,
        bankBagNumber: string,
        clockOutUser: boolean
    ) => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Shift/close`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    actualCashCount,
                    varianceNotes,
                    varianceApprovedBy,
                    nextShiftFloat,
                    bankDeposit,
                    bankBagNumber,
                    clockOutUser,
                }),
            });

            if (response.ok) {
                // Get the closed shift data from the API response
                const closedShiftData: Shift = await response.json();
                
                // Store the closed shift data for Z Report display
                setClosedShift(closedShiftData);
                
                // Move to complete step to show Z Report
                setCurrentStep('complete');
            } else {
                const errorText = await response.text();
                console.error('Failed to close shift:', errorText);
                setHeaderTitle("Error");
                setContent("Failed to end day. Please try again.");
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Error closing shift:', error);
            setHeaderTitle("Error");
            setContent("An error occurred while ending the day.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } finally {
            setShowSpinner(false);
        }
    };

    const handleResolveOrders = () => {
        navigate('/tables?mode=zreport&openOrders=' + openOrders.length);
    };

    const handleCancel = () => {
        if (currentStep === 'deposit') {
            setCurrentStep('review');
        } else if (currentStep === 'review') {
            setCurrentStep('count');
        } else {
            navigate('/profile');
        }
    };

    const saveXReport = async (shiftData: Shift) => {
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/User/save-xreport`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    shiftId: shiftData.shiftId,
                    shiftStartDate: shiftData.startDate,
                    shiftEndDate: shiftData.endDate,
                    grossSales: shiftData.grossSales ?? 0,
                    discounts: shiftData.discounts ?? 0,
                    mealPlanDeductions: shiftData.mealPlanDeductions ?? 0,
                    returns: shiftData.returns ?? 0,
                    netSales: shiftData.netSales ?? 0,
                    taxes: shiftData.taxes ?? 0,
                    serviceCharge: shiftData.serviceCharge ?? 0,
                    totalRevenue: shiftData.totalRevenue ?? 0,
                    expectedCash: shiftData.expectedCash ?? 0,
                    creditCards: shiftData.creditCards ?? 0,
                    roomCharges: shiftData.roomCharges ?? 0,
                    numberOfReceipts: shiftData.numberOfReceipts ?? 0,
                    numberOfVoidOrders: shiftData.numberOfVoidOrders ?? 0,
                    firstOrderNumber: shiftData.firstOrderNumber,
                    lastOrderNumber: shiftData.lastOrderNumber,
                }),
            });
        } catch (error) {
            console.error('Error saving X-Report:', error);
        }
    };

    const handleXReport = () => {
        // Persist the X-Report so it shows up in the Web Admin closing reports.
        // (Display still opens immediately; the save runs in the background.)
        if (currentShift) {
            saveXReport(currentShift);
        }
        setIsXReportOpen(true);
    };

    const handleCountTabClick = () => {
        setSelectedTab('count');
        setIsCashCalculatorOpen(true);
    };

    const handleApplyCashCount = (total: number) => {
        setActualCashCount(total);
    };

    // Z Report action handlers
    const handleSaveZReport = async () => {
        try {
            setShowSpinner(true);
            
            const receiptElement = zReportRef.current?.getReceiptElement();
            if (!receiptElement) {
                throw new Error('Receipt element not found');
            }

            // Generate canvas from the receipt element
            const canvas = await html2canvas(receiptElement, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });

            // Convert to PNG and download
            const link = document.createElement('a');
            link.download = `z-report-${closedShift?.shiftId || 'unknown'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            
            setHeaderTitle("Success!");
            setContent("Z Report saved as PNG.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } catch (error) {
            console.error('Error saving Z Report:', error);
            setHeaderTitle("Error");
            setContent("Failed to save Z Report.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } finally {
            setShowSpinner(false);
        }
    };

    const handleEmailZReport = () => {
        setIsEmailPopupOpen(true);
    };

    const handleSendEmail = async (to: string, subject: string, message: string) => {
        const receiptElement = zReportRef.current?.getReceiptElement();
        if (!receiptElement) {
            throw new Error('Receipt element not found');
        }

        // Generate canvas from the receipt element
        const canvas = await html2canvas(receiptElement, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
        });

        // Convert to base64 PNG
        const imageData = canvas.toDataURL('image/png').split(',')[1];

        const response = await fetch(`${process.env.REACT_APP_API_URL}/Shift/email-zreport`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                shiftId: closedShift?.shiftId,
                toEmail: to,
                subject: subject,
                message: message,
                attachmentBase64: imageData,
            }),
        });
        
        if (!response.ok) {
            throw new Error('Failed to send email');
        }

        setHeaderTitle("Success!");
        setContent("Z Report sent via email.");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
    };

    const handlePrintZReport = () => {
        window.print();
    };

    const handleNewShift = () => {
        navigate('/profile');
    };

    const formatCurrency = (value: number) => {
        return formatPrice(value);
    };

    const currentSales = currentShift?.totalRevenue ?? 0;
    const creditCards = currentShift?.creditCards ?? 0;
    const roomCharges = currentShift?.roomCharges ?? 0;

    // Show Z Report View after shift is closed
    if (currentStep === 'complete') {
        return (
            <>
                <ZReportView
                    ref={zReportRef}
                    shift={closedShift}
                    property={currentProperty}
                    onSave={handleSaveZReport}
                    onEmail={handleEmailZReport}
                    onPrint={handlePrintZReport}
                    onNewShift={handleNewShift}
                />
                <EmailReportPopup
                    isOpen={isEmailPopupOpen}
                    onClose={() => setIsEmailPopupOpen(false)}
                    onSend={handleSendEmail}
                    shiftId={closedShift?.shiftId || '000000'}
                    cashierName={closedShift?.closedByUserWithName || closedShift?.startedByUserWithName || 'N/A'}
                    netSales={closedShift?.netSales || 0}
                    defaultEmail={''}
                />
                {showSpinner && <Spinner />}
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </>
        );
    }

    // Show Finalize Deposit screen
    if (currentStep === 'deposit') {
        return (
            <>
                <FinalizeDeposit
                    shift={currentShift}
                    property={currentProperty}
                    onCloseShift={handleFinalizeDeposit}
                    onCancel={handleCancel}
                />
                {showSpinner && <Spinner />}
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </>
        );
    }

    // Show Review Variance screen
    if (currentStep === 'review') {
        return (
            <>
                <ReviewVariance
                    shift={currentShift}
                    property={currentProperty}
                    actualCashCount={actualCashCount}
                    onNext={handleVarianceNext}
                    onCancel={handleCancel}
                />
                {showSpinner && <Spinner />}
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </>
        );
    }

    return (
        <div className="close-shift-page">
            <div className="close-shift-container">
                {/* Left Section */}
                <div className="close-shift-main">
                    {/* Header */}
                    <div className="close-shift-header">
                        <div>
                            <h3>End Day: Blind Count</h3>
                            <p className="subtitle">Please count the drawer and verify totals.</p>
                        </div>

                        {openOrders.length > 0 && (
                            <div className="open-orders-warning">
                                <div className="warning-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <div className="warning-text">
                                    <span className="warning-title">{openOrders.length} Open Orders</span>
                                    <span className="warning-subtitle">Must settle to close.</span>
                                </div>
                                <button className="resolve-btn" onClick={handleResolveOrders}>
                                    Resolve Now
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Cards Section */}
                    <div className="close-shift-cards">
                        {/* Actual Cash Count Card */}
                        <div className="shift-card cash-count-card">
                            <div className="card-header">
                                <span className="card-title">Actual Cash Count</span>
                                <span className="card-icon">{getCurrencySymbol()}</span>
                            </div>
                            <div className="cash-input-container">
                                <span className="currency-symbol">{getCurrencySymbol()}</span>
                                <input
                                    type="number"
                                    className="cash-input"
                                    value={actualCashCount}
                                    onChange={(e) => setActualCashCount(parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                />
                            </div>
                            <div className="cash-tabs">
                                <button
                                    className={`cash-tab ${selectedTab === 'count' ? 'active' : ''}`}
                                    onClick={handleCountTabClick}
                                >
                                    COUNT
                                </button>
                                <button
                                    className={`cash-tab ${selectedTab === 'payouts' ? 'active' : ''}`}
                                    onClick={() => setSelectedTab('payouts')}
                                >
                                    PAYOUTS
                                </button>
                            </div>
                        </div>

                        {/* Credit Cards Card */}
                        <div className="shift-card" style={{ backgroundColor: '#f0f6ff' }}>
                            <div className="card-header">
                                <span className="card-title" style={{ color: '#354d96' }}>Credit Cards</span>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="5" width="20" height="14" rx="2" stroke="#95c3fb" strokeWidth="1.5" />
                                    <path d="M2 10H22" stroke="#95c3fb" strokeWidth="1.5" />
                                </svg>
                            </div>
                            <div className="card-value" style={{ color: '#354d96' }}>{formatCurrency(creditCards)}</div>
                        </div>

                        {/* Room Charges Card */}
                        <div className="shift-card" style={{ backgroundColor: '#faf6ff' }}>
                            <div className="card-header">
                                <span className="card-title" style={{ color: '#936cb2' }}>Room Charges</span>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 21H21M4 18H20M6 18V13M10 18V13M14 18V13M18 18V13M12 3L20 8V13H4V8L12 3Z" stroke="#936cb2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className="card-value" style={{ color: '#936cb2' }}>{formatCurrency(roomCharges)}</div>
                        </div>
                    </div>
                </div>

                {/* Right Section */}
                <div className="close-shift-sidebar">
                    <div className="shift-info">
                        <h3 className="shift-number">Z#{currentShift?.shiftId || '000000'}</h3>
                        <p className="company-name">{currentProperty?.displayName}</p>
                    </div>

                    <div className="current-sales">
                        <span>Current Sales:</span>
                        <span className="sales-value">{formatCurrency(currentSales)}</span>
                    </div>

                    <div className="sidebar-actions">
                        <button
                            className="review-shift-btn"
                            onClick={handleReviewShift}
                            disabled={openOrders.length > 0}
                        >
                            Review Shift
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <div className="secondary-actions">
                            <button className="secondary-btn xreport-btn" onClick={handleXReport}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 18H4C2.89543 18 2 17.1046 2 16V11C2 9.89543 2.89543 9 4 9H20C21.1046 9 22 9.89543 22 11V16C22 17.1046 21.1046 18 20 18H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                X-Report
                            </button>
                            <button className="secondary-btn cancel" onClick={handleCancel}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showSpinner && <Spinner />}
            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}

            <CashCalculatorPopup
                isOpen={isCashCalculatorOpen}
                onClose={() => setIsCashCalculatorOpen(false)}
                onApply={handleApplyCashCount}
                bills={bills}
                setBills={setBills}
                looseCoins={looseCoins}
                setLooseCoins={setLooseCoins}
            />

            <XReportPopup
                isOpen={isXReportOpen}
                onClose={() => setIsXReportOpen(false)}
                shift={currentShift}
                property={currentProperty}
            />
        </div>
    );
};

export default CloseShift;